import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface DropdownItemBadgesProps {
  isUnavailable?: boolean;
  isNonTeamMember?: boolean;
  isUnverified?: boolean;
  renderAsSecondaryContent?: boolean;
}

/*
 * Style.
 */

const StyledBadgesDiv = styled.div<{$renderAsSecondaryContent: boolean}>`
  grid-area: ${(p) => (p.$renderAsSecondaryContent ? 'secondary-content' : 'badges')};
  margin: 0 6px;
  ${center()};
`;

const StyledBadgesWrapperDiv = styled.div`
  display: grid;
  grid-gap: 4px;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  min-width: max-content;
  align-items: center;
`;

const StyledBadgeDiv = styled.div`
  padding: 2px 5px;

  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.bold};
  letter-spacing: -0.15px;

  border-radius: 100px;

  & > div {
    transform: translateY(-0.5px);
  }
`;

const StyledOutOfOfficeDiv = styled(StyledBadgeDiv)`
  color: ${(p) => p.theme.palette.red.shade40};
  background-color: ${(p) => p.theme.palette.red.shade20};
`;

const StyledNotInTeamDiv = styled(StyledBadgeDiv)`
  color: ${(p) => p.theme.greys.shade80};
  background-color: ${(p) => p.theme.alphas.gray20};
`;

const StyledUnverifiedDiv = styled(StyledBadgeDiv)`
  color: ${(p) => p.theme.greys.shade80};
  background-color: ${(p) => p.theme.alphas.gray20};
`;

/*
 * Component.
 */

export const DropdownItemBadges: FC<DropdownItemBadgesProps> = (props) => {
  const {isUnavailable, isNonTeamMember, isUnverified} = props;
  if (!isUnavailable && !isNonTeamMember && !isUnverified) {
    return null;
  }

  // If teammate is out of office and unverified, then only show unverified badge, so we show at most two badges total.
  return (
    <StyledBadgesDiv $renderAsSecondaryContent={Boolean(props.renderAsSecondaryContent)}>
      <TooltipCoordinator placement="top" render={() => renderTooltip(props)}>
        <StyledBadgesWrapperDiv>
          {isUnverified && renderUnverifiedBadge()}
          {!isUnverified && isUnavailable && renderOutOfOfficeBadge()}
          {isNonTeamMember && renderNotInTeamBadge()}
        </StyledBadgesWrapperDiv>
      </TooltipCoordinator>
    </StyledBadgesDiv>
  );
};

/*
 * Helpers.
 */

function renderOutOfOfficeBadge() {
  return (
    <StyledOutOfOfficeDiv>
      <div>
        <Trans id="dropdown-item-outofoffice">Out of office</Trans>
      </div>
    </StyledOutOfOfficeDiv>
  );
}

function renderNotInTeamBadge() {
  return (
    <StyledNotInTeamDiv>
      <div>
        <Trans id="dropdown-item-notinteam">Not in team</Trans>
      </div>
    </StyledNotInTeamDiv>
  );
}

function renderUnverifiedBadge() {
  return (
    <StyledUnverifiedDiv>
      <div>
        <Trans id="dropdown-item-unverified">Unverified</Trans>
      </div>
    </StyledUnverifiedDiv>
  );
}

function renderTooltip(props: DropdownItemBadgesProps) {
  const {isUnverified} = props;

  // We are only rendering tooltip for unverified teammates currently.
  if (!isUnverified) {
    return null;
  }

  return (
    <Trans id="dropdown-item-unverified-tooltip">
      This user signed up using domain login and needs to be verified. Any company admin can do so by visiting
      the “Teammates” section of Settings.
    </Trans>
  );
}
